import axios, {AxiosHeaders, type AxiosRequestConfig} from 'axios';
import {fetchVersionInfo} from '../libs/version';
import {store} from '../store';
import {getUtmFirstHeaders} from '../libs/utm';

type JsonRecord = Record<string, unknown>;
function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null;
}

function parseJsonRecord(value: string): JsonRecord | undefined {
  try {
    const parsed: unknown = JSON.parse(value);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function toAxiosHeaders(headers: unknown): AxiosHeaders {
  if (headers instanceof AxiosHeaders) {
    return headers;
  }

  if (typeof headers === 'string' || headers === undefined) {
    return AxiosHeaders.from(headers);
  }

  if (isRecord(headers)) {
    const normalized: Record<string, string> = {};
    for (const [key, value] of Object.entries(headers)) {
      if (typeof value === 'string') {
        normalized[key] = value;
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        normalized[key] = String(value);
      }
    }

    return AxiosHeaders.from(normalized);
  }

  return AxiosHeaders.from(undefined);
}

function getErrorResponse(
  error: unknown,
): {status?: number; data?: unknown} | undefined {
  if (!isRecord(error) || !isRecord(error.response)) {
    return undefined;
  }

  return error.response as {status?: number; data?: unknown};
}

function getErrorConfig(error: unknown): AxiosRequestConfig | undefined {
  if (!isRecord(error) || !isRecord(error.config)) {
    return undefined;
  }

  return error.config as AxiosRequestConfig;
}

class AppOutdatedError extends Error {
  readonly code = 'APP_OUTDATED' as const;

  constructor() {
    super(
      'A new version of the app is available. Please refresh the page and try again.',
    );
    this.name = 'AppOutdatedError';
  }
}

const authLogEnabled =
  (globalThis.window !== undefined &&
    globalThis.localStorage?.getItem('AUTH_DEBUG') === '1') ||
  process.env.NODE_ENV !== 'production';

function maskToken(t?: string): string {
  if (!t) {
    return 'null';
  }

  try {
    const s = String(t);
    if (s.length <= 12) {
      return '***';
    }

    return `${s.slice(0, 4)}…${s.slice(-4)}`;
  } catch {
    return '***';
  }
}

function authLog(...args: unknown[]): void {
  if (authLogEnabled) {
    console.log('[auth]', ...args);
  }
}

/**
 * Determine the current frontend build id. Prefer Redux (hydrate from version slice),
 * fall back to environment variables / globals if the store is not ready yet.
 */
function getCurrentBuildId(): string | undefined {
  try {
    const state = store.getState();
    const reduxBuildId = state?.version?.data?.buildId;
    if (reduxBuildId) {
      return reduxBuildId;
    }
  } catch {
    // Store might not be initialized yet
  }

  const importMeta = import.meta as ImportMeta & {
    env?: {VITE_BUILD_ID?: string};
  };
  const globalWindow = globalThis as typeof globalThis & {
    __APP_BUILD_ID?: string;
  };

  return (
    importMeta.env?.VITE_BUILD_ID ??
    (typeof process === 'undefined'
      ? undefined
      : process.env.REACT_APP_BUILD_ID) ??
    (globalThis.window === undefined ? undefined : globalWindow.__APP_BUILD_ID)
  );
}

/**
 * Fetch version.json and throw if the current bundle is outdated.
 * Network errors are ignored so we don't block auth flows when offline.
 * However, the APP_OUTDATED error is rethrown so callers can block sensitive flows.
 */
export async function checkAppVersionFreshOrThrow(): Promise<void> {
  try {
    const latest = await fetchVersionInfo();
    const latestBuildId = latest?.buildId ?? '';
    const currentBuildId = getCurrentBuildId();
    if (currentBuildId && latestBuildId && latestBuildId !== currentBuildId) {
      console.error(
        'A new version of the app is available.  Please refresh the page and try again',
      );
      throw new AppOutdatedError();
    }
  } catch (error) {
    if (error instanceof AppOutdatedError) {
      throw error;
    }
    // ignore network/parsing errors — do not block login/signup on transient issues
  }
}

export type AuthState = {
  // Token is the current access (JWT) token
  token: string | undefined;
  // RefreshToken is optional and used to refresh the access token
  refreshToken?: string | undefined;
  email: string | undefined;
  emailVerified: boolean | undefined;
  // Whether the user's account is disabled (null = unknown)
  disabled: boolean | undefined;
  // Whether the user's account is banned (null = unknown)
  banned: boolean | undefined;
  // Whether the user's account is activated (null = unknown)
  activated: boolean | undefined;
  // Whether the user's account is a beta user (null = unknown)
  betaUser: boolean | undefined;
  csrfToken: string | undefined;
};

const accessTokenKey = 'auth_access_token';
const refreshTokenKey = 'auth_refresh_token';
const emailKey = 'auth_email';
const emailVerifiedKey = 'auth_email_verified';
const userDisabledKey = 'auth_user_disabled';
const csrfKey = 'csrf_token';

let accessToken: string | undefined =
  localStorage.getItem(accessTokenKey) ?? undefined;
let refreshToken: string | undefined =
  localStorage.getItem(refreshTokenKey) ?? undefined;
let email: string | undefined = localStorage.getItem(emailKey) ?? undefined;
let emailVerified: boolean | undefined = ((): boolean | undefined => {
  const v = localStorage.getItem(emailVerifiedKey);
  // Default to true when not present in localStorage so the UI treats the user as verified
  // until the backend is queried.
  if (v === null) {
    return true;
  }

  return v === 'true';
})();
let disabled: boolean | undefined = (() => {
  const v = localStorage.getItem(userDisabledKey);
  // Default to false when not present in localStorage so the UI treats the user as not disabled
  // until the backend is queried.
  if (v === null) {
    return false;
  }

  try {
    const parsed = parseJsonRecord(v);
    return parsed?.disabled === true;
  } catch {
    return false;
  }
})();
let banned: boolean | undefined = (() => {
  const v = localStorage.getItem(userDisabledKey);
  // Default to false when not present in localStorage so the UI treats the user as not banned
  // until the backend is queried.
  if (v === null) {
    return false;
  }

  try {
    const parsed = parseJsonRecord(v);
    return parsed?.banned === true;
  } catch {
    return false;
  }
})();
let activated: boolean | undefined = (() => {
  const v = localStorage.getItem(userDisabledKey);
  if (v === null) {
    return true;
  }

  try {
    const parsed = parseJsonRecord(v);
    return parsed?.activated === true;
  } catch {
    return false;
  }
})();
let betaUser: boolean | undefined = (() => {
  const v = localStorage.getItem(userDisabledKey);
  // Default to false when not present in localStorage so the UI treats the user as not a beta user
  // until the backend is queried.
  if (v === null) {
    return false;
  }

  try {
    const parsed = parseJsonRecord(v);
    return parsed?.beta_user === true;
  } catch {
    return false;
  }
})();
let csrfToken: string | undefined = localStorage.getItem(csrfKey) ?? undefined;

type Listener = (s: AuthState) => void;
let listeners: Listener[] = [];

function notify(): void {
  const s = getAuthState();
  for (const l of listeners) {
    l(s);
  }
}

let loginLoading = false;
type LoadingListener = (v: boolean) => void;
let loginLoadingListeners: LoadingListener[] = [];

function notifyLoginLoading(): void {
  for (const l of loginLoadingListeners) {
    l(loginLoading);
  }
}

export function addLoginLoadingListener(fn: LoadingListener): () => void {
  loginLoadingListeners.push(fn);
  return () => {
    loginLoadingListeners = loginLoadingListeners.filter(f => f !== fn);
  };
}

export function getLoginLoading(): boolean {
  return loginLoading;
}

function setLoginLoading(v: boolean): void {
  loginLoading = v;
  notifyLoginLoading();
}

// Track whether an auth token refresh is in progress (similar to loginLoading).
let refreshAuthLoading = false;
type RefreshLoadingListener = (v: boolean) => void;
let refreshAuthLoadingListeners: RefreshLoadingListener[] = [];

function notifyRefreshAuthLoading(): void {
  for (const l of refreshAuthLoadingListeners) {
    l(refreshAuthLoading);
  }
}

export function addRefreshAuthLoadingListener(
  fn: RefreshLoadingListener,
): () => void {
  refreshAuthLoadingListeners.push(fn);
  return () => {
    refreshAuthLoadingListeners = refreshAuthLoadingListeners.filter(
      f => f !== fn,
    );
  };
}

export function getRefreshAuthLoading(): boolean {
  return refreshAuthLoading;
}

function setRefreshAuthLoading(v: boolean): void {
  refreshAuthLoading = v;
  notifyRefreshAuthLoading();
}

export function addAuthListener(fn: Listener): () => void {
  listeners.push(fn);
  return () => {
    listeners = listeners.filter(l => l !== fn);
  };
}

// Sync auth state across browser tabs/windows using the storage event.
if (globalThis.window !== undefined) {
  globalThis.addEventListener('storage', (event: StorageEvent) => {
    if (!event.key) {
      return;
    }

    const keys = [
      accessTokenKey,
      refreshTokenKey,
      emailKey,
      emailVerifiedKey,
      userDisabledKey,
      csrfKey,
    ];
    if (!keys.includes(event.key)) {
      return;
    }

    // Pull latest values from localStorage into memory and notify listeners.
    accessToken = localStorage.getItem(accessTokenKey) ?? undefined;
    refreshToken = localStorage.getItem(refreshTokenKey) ?? undefined;
    email = localStorage.getItem(emailKey) ?? undefined;
    const ev = localStorage.getItem(emailVerifiedKey);
    // Default to true when not present in localStorage.
    emailVerified = ev === null ? true : ev === 'true';
    const ud = localStorage.getItem(userDisabledKey);
    if (ud === null) {
      // Default values when not present: disabled/banned/betaUser => false, activated => true.
      disabled = false;
      banned = false;
      activated = true;
      betaUser = false;
    } else {
      try {
        const parsed = parseJsonRecord(ud);
        disabled = parsed?.disabled === true;
        banned = parsed?.banned === true;
        activated = parsed?.activated === true;
        betaUser = parsed?.beta_user === true;
      } catch {
        disabled = false;
        banned = false;
        activated = true;
        betaUser = false;
      }
    }

    csrfToken = localStorage.getItem(csrfKey) ?? undefined;
    notify();
  });
}

export function getAuthState(): AuthState {
  return {
    token: accessToken,
    refreshToken,
    email,
    emailVerified,
    disabled,
    banned,
    activated,
    betaUser,
    csrfToken,
  };
}

export function setEmailVerified(newEmailVerified: boolean): void {
  emailVerified = newEmailVerified;
  localStorage.setItem(emailVerifiedKey, newEmailVerified ? 'true' : 'false');
  notify();
}

export function setUserDisabled(
  newDisabled: boolean,
  newBanned: boolean,
  newActivated: boolean,
  newBetaUser: boolean,
): void {
  disabled = newDisabled;
  banned = newBanned;
  activated = newActivated;
  betaUser = newBetaUser;
  localStorage.setItem(
    userDisabledKey,
    JSON.stringify({
      disabled: Boolean(newDisabled),
      banned: Boolean(newBanned),
      activated: Boolean(newActivated),
      beta_user: Boolean(newBetaUser), // eslint-disable-line @typescript-eslint/naming-convention
    }),
  );
  notify();
}

/**
 * Set authentication state.
 * newAccessToken: access (JWT) token
 * newRefreshToken: refresh token (may be null)
 * newEmail: user's email (may be null)
 * newEmailVerified: boolean|null
 * newCsrf: csrf token (may be null)
 */
function setAuth(
  newAccessToken: string | undefined,
  newRefreshToken: string | undefined,
  newEmail: string | undefined,
  newEmailVerified: boolean | undefined,
  newDisabled: boolean | undefined,
  newBanned: boolean | undefined,
  newActivated: boolean | undefined,
  newBetaUser: boolean | undefined,
  newCsrf: string | undefined,
): void {
  accessToken = newAccessToken;
  refreshToken = newRefreshToken;
  email = newEmail;
  emailVerified = newEmailVerified;
  disabled = newDisabled;
  banned = newBanned;
  activated = newActivated;
  betaUser = newBetaUser;
  csrfToken = newCsrf;

  if (newAccessToken === null || newAccessToken === undefined) {
    localStorage.removeItem(accessTokenKey);
  } else {
    localStorage.setItem(accessTokenKey, newAccessToken);
  }

  if (newRefreshToken === null || newRefreshToken === undefined) {
    localStorage.removeItem(refreshTokenKey);
  } else {
    localStorage.setItem(refreshTokenKey, newRefreshToken);
  }

  if (newEmail === null || newEmail === undefined) {
    localStorage.removeItem(emailKey);
  } else {
    localStorage.setItem(emailKey, newEmail);
  }

  if (newEmailVerified === null || newEmailVerified === undefined) {
    localStorage.removeItem(emailVerifiedKey);
  } else {
    localStorage.setItem(emailVerifiedKey, newEmailVerified ? 'true' : 'false');
  }

  if (newDisabled === null) {
    localStorage.removeItem(userDisabledKey);
  } else {
    localStorage.setItem(
      userDisabledKey,
      JSON.stringify({
        disabled: Boolean(newDisabled),
        banned: Boolean(newBanned),
        activated: Boolean(newActivated),
        beta_user: Boolean(newBetaUser), // eslint-disable-line @typescript-eslint/naming-convention
      }),
    );
  }

  if (newCsrf === null || newCsrf === undefined) {
    localStorage.removeItem(csrfKey);
  } else {
    localStorage.setItem(csrfKey, newCsrf);
  }

  notify();
}

function clearAuth(): void {
  accessToken = undefined;
  refreshToken = undefined;
  email = undefined;
  // After clearing auth, revert emailVerified/disabled/banned to their initial defaults
  // (verified = true, disabled = false, banned = false, activated = true, betaUser = false) until the API is queried.
  emailVerified = true;
  disabled = false;
  banned = false;
  activated = true;
  betaUser = false;
  csrfToken = undefined;
  localStorage.removeItem(accessTokenKey);
  localStorage.removeItem(refreshTokenKey);
  localStorage.removeItem(emailKey);
  localStorage.removeItem(emailVerifiedKey);
  localStorage.removeItem(userDisabledKey);
  localStorage.removeItem(csrfKey);
  notify();
}

function randomToken(bytes = 32): string {
  const array = new Uint8Array(bytes);
  const cryptoApi =
    globalThis.crypto ??
    (globalThis as typeof globalThis & {msCrypto?: Crypto}).msCrypto;

  if (!cryptoApi) {
    throw new Error('Crypto API unavailable');
  }

  cryptoApi.getRandomValues(array);
  return Object.values(array)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');
}

function extractTokens(data: unknown): {
  accessToken: string | undefined;
  refreshToken: string | undefined;
} {
  if (!data) {
    return {accessToken: undefined, refreshToken: undefined};
  }

  if (typeof data === 'string') {
    return {accessToken: data, refreshToken: undefined};
  }

  if (!isRecord(data)) {
    return {accessToken: undefined, refreshToken: undefined};
  }

  const accessCandidates = [
    'access_token',
    'token',
    'bearer_token',
    'bearerToken',
  ];
  const refreshCandidates = ['refresh_token', 'refreshToken'];
  let a: string | undefined;
  let r: string | undefined;
  for (const k of accessCandidates) {
    const value = data[k];
    if (typeof value === 'string') {
      a = value;
      break;
    }
  }

  for (const k of refreshCandidates) {
    const value = data[k];
    if (typeof value === 'string') {
      r = value;
      break;
    }
  }

  return {accessToken: a, refreshToken: r};
}

/**
 * Attach axios interceptors once at module load.
 *
 * Request interceptor adds Authorization and X-CSRF-Token when available.
 * Response interceptor will attempt to refresh tokens (using refreshToken) on 401/403,
 * retry the failed request once after refresh, and fall back to clearing auth and redirecting to login.
 */
axios.interceptors.request.use(config => {
  const headers = toAxiosHeaders(config.headers);
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  if (csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  // Attach first-touch UTM headers for server-side attribution
  try {
    const utmHdrs = getUtmFirstHeaders();
    for (const [k, v] of Object.entries(utmHdrs)) {
      headers.set(k, v);
    }
  } catch {}

  config.headers = headers;

  // Debug logging for refresh requests
  try {
    const url = String(config.url ?? '');
    if (url.includes('/api/refresh_auth')) {
      const hdrs = toAxiosHeaders(config.headers);
      const p = isRecord(config.params) ? config.params : {};
      const hasAuth = Boolean(hdrs.get('Authorization'));
      const skipHdr = hdrs.get('X-SKIP-AUTH-LOGOUT');
      authLog('request -> /api/refresh_auth', {
        hasAuthorizationHeader: hasAuth,
        xSkipAuthLogoutHeader: skipHdr,
        params: {
          ...p,
          token: maskToken(typeof p.token === 'string' ? p.token : undefined),
        },
      });
    }
  } catch {}

  return config;
});

let refreshPromise: Promise<void> | undefined;

axios.interceptors.response.use(
  resp => resp,
  async error => {
    const response = getErrorResponse(error);
    const originalRequest = getErrorConfig(error);
    if (!originalRequest) {
      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Request failed');
    }

    const status = response?.status;
    const url = String(originalRequest.url ?? '');
    const requestHeaders = toAxiosHeaders(originalRequest.headers);
    const skipLogoutLog = Boolean(requestHeaders.get('X-SKIP-AUTH-LOGOUT'));
    const skipLogout = requestHeaders.get('X-SKIP-AUTH-LOGOUT');

    if (status === 401 || status === 403) {
      authLog('response error', {
        status,
        url,
        skipLogout: skipLogoutLog,
        hasRefreshToken: Boolean(refreshToken),
      });
    }

    // Only attempt refresh on auth errors and when not explicitly skipped.
    if (!skipLogout && (status === 401 || status === 403)) {
      // If we have a refresh token, attempt to refresh tokens and retry original request.
      if (refreshToken) {
        // Ensure only one refresh request is in-flight at a time.
        if (!refreshPromise) {
          setRefreshAuthLoading(true);
          authLog('refresh starting', {token: maskToken(refreshToken)});
          refreshPromise = (async () => {
            try {
              // Call refresh endpoint with the refresh token; skip logout handling for this internal call
              const response = await axios.post('/api/refresh_auth', null, {
                params: {token: refreshToken},
                headers: {'X-SKIP-AUTH-LOGOUT': true},
              });
              authLog('refresh response', {
                status: response?.status,
                ok: Boolean(response?.data),
              });
              const extracted = extractTokens(response?.data);
              const newAccess = extracted.accessToken;
              const newRefresh = extracted.refreshToken ?? refreshToken;
              if (!newAccess) {
                throw new Error(
                  'Refresh response did not include an access token',
                );
              }

              const csrf = randomToken();
              // Keep email as-is (server does not return it on refresh), emailVerified/disabled/banned remain as-is
              setAuth(
                newAccess,
                newRefresh,
                email,
                emailVerified,
                disabled,
                banned,
                activated,
                betaUser,
                csrf,
              );
              authLog('refresh success', {
                access: maskToken(newAccess),
                refresh: maskToken(newRefresh),
              });
            } catch (error_) {
              const refreshErrorResponse = getErrorResponse(error_);
              const s = refreshErrorResponse?.status;
              const d = refreshErrorResponse?.data;
              authLog('refresh failed', {status: s, data: d});
              // Failed to refresh -> clear auth and redirect to login
              clearAuth();
              if (
                globalThis.window !== undefined &&
                globalThis.location.pathname !== '/login'
              ) {
                const next = encodeURIComponent(
                  globalThis.location.pathname + globalThis.location.search,
                );
                globalThis.location.assign(`/login?next=${next}`);
              }

              throw error_;
            } finally {
              refreshPromise = undefined;
              setRefreshAuthLoading(false);
              authLog('refresh finished');
            }
          })();
        }

        await refreshPromise;
        // Retry the original request with the new access token
        const retryHeaders = toAxiosHeaders(originalRequest.headers);
        if (accessToken) {
          retryHeaders.set('Authorization', `Bearer ${accessToken}`);
        }

        originalRequest.headers = retryHeaders;

        authLog('retrying original request', {
          url: String(originalRequest.url ?? ''),
        });
        return axios(originalRequest);
      }

      // No refresh token available, clear auth and redirect to login.
      clearAuth();
      if (
        globalThis.window !== undefined &&
        globalThis.location.pathname !== '/login'
      ) {
        const next = encodeURIComponent(
          globalThis.location.pathname + globalThis.location.search,
        );
        globalThis.location.assign(`/login?next=${next}`);
      }
    }

    if (error instanceof Error) {
      throw error;
    }

    throw new Error('Request failed');
  },
);

type LoginResponse = {
  [key: string]: unknown;
  token?: string;
  access_token?: string;
  bearer_token?: string;
  bearerToken?: string;
  email_verified?: boolean | undefined;
};

/**
 * Create a new user via the API.
 * The backend expects a plaintext `password` field in the request body
 * (the API will hash it before storing). The backend now requires the
 * user to agree to terms at creation time via `terms_agreed`.
 *
 * Returns the created user response object from the API.
 */

/**
 * Login with email/password. On success stores access and refresh tokens.
 * This sends credentials as application/x-www-form-urlencoded form data.
 */
export async function login(
  emailParameter: string,
  passwordParameter: string,
): Promise<void> {
  setLoginLoading(true);
  try {
    // Ensure user is on the latest frontend before attempting login
    await checkAppVersionFreshOrThrow();
    // Build form body for application/x-www-form-urlencoded
    const form = new URLSearchParams();
    form.append('username', emailParameter);
    form.append('password', passwordParameter);

    const csrf = randomToken();
    // Leave emailVerified/disabled/banned/activated/beta flags unknown until server confirms
    clearAuth();
    setAuth(
      undefined,
      undefined,
      emailParameter,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      csrf,
    );

    const response = await axios.post<LoginResponse>(
      '/api/login',
      form.toString(),
      {
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      },
    );

    const extracted = extractTokens(response?.data);
    const access = extracted.accessToken;
    const refresh = extracted.refreshToken ?? undefined;

    if (!access) {
      throw new Error('Login response did not include an access token');
    }

    // Keep the verification/disabled/ban/activation/beta flags unknown until the server checks complete.
    setAuth(
      access,
      refresh,
      emailParameter,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      csrf,
    );

    // After successful login, attempt to fetch whether the user's email is verified and whether the user is disabled.
    // These checks should not block login if they fail.
    try {
      await Promise.all([fetchUserDisabled(), fetchEmailVerified()]);
    } catch {
      // ignore errors — leave emailVerified/disabled/banned as-is (null or whatever fetch set)
    }
  } finally {
    // Stop the login loading indicator only after tokens are set and the follow-up checks have run (or on error).
    setLoginLoading(false);
  }
}

/**
 * Logout the current session. Attempts to notify backend (if supported),
 * but always clears local authentication state.
 */
export async function logout(): Promise<void> {
  try {
    await axios.post('/api/logout', null, {
      params: {refresh_token: refreshToken}, // eslint-disable-line @typescript-eslint/naming-convention
      headers: {'X-SKIP-AUTH-LOGOUT': true},
    });
  } catch {
    // ignore network/API errors on logout
  } finally {
    clearAuth();
  }
}

/**
 * Change the current user's password.
 * Calls POST /api/change_password with current_password and new_password as query params.
 * The request is authenticated using the axios interceptors (Authorization header).
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await axios.post('/api/change_password', null, {
    params: {current_password: currentPassword, new_password: newPassword}, // eslint-disable-line @typescript-eslint/naming-convention
  });
}

/**
 * Create a new user via the API.
 */
export async function createUser(
  emailParameter: string,
  passwordParameter: string,
  termsAgreed: boolean,
  betaKey?: string,
): Promise<unknown> {
  // Block signup if this frontend bundle is outdated
  await checkAppVersionFreshOrThrow();

  const body = {
    id: '',
    email: emailParameter,
    password: passwordParameter,
    terms_agreed: Boolean(termsAgreed), // eslint-disable-line @typescript-eslint/naming-convention
  };
  const parameters = {beta_key: typeof betaKey === 'string' ? betaKey : ''}; // eslint-disable-line @typescript-eslint/naming-convention
  try {
    const response = await axios.post('/api/users', body, {
      params: parameters,
      headers: {'X-SKIP-AUTH-LOGOUT': true},
    });
    return response.data;
  } catch (error: unknown) {
    const response = getErrorResponse(error);
    const status = response?.status;
    const data = response?.data;
    if (process.env.NODE_ENV !== 'production') {
      console.error('createUser failed:', {status, data});
    }

    throw error;
  }
}

/**
 * Fetch whether the current user's email is verified from the dedicated endpoint.
 * Sets local emailVerified state and returns the boolean result.
 */
export async function fetchEmailVerified(): Promise<boolean> {
  const response = await axios.post('/api/is_email_verified', null, {
    headers: {'X-SKIP-AUTH-LOGOUT': true},
  });
  const verified = Boolean(response?.data);
  setEmailVerified(verified);

  if (process.env.NODE_ENV !== 'production') {
    console.log('Email verified:', verified);
  }

  return verified;
}

export async function fetchUserDisabled(): Promise<{
  disabled: boolean;
  banned: boolean;
  activated: boolean;
  betaUser: boolean;
}> {
  const response = await axios.post('/api/is_user_disabled', null, {
    headers: {'X-SKIP-AUTH-LOGOUT': true},
  });
  const data = isRecord(response?.data) ? response.data : {};
  const disabledResponse = Boolean(data.disabled);
  const bannedResponse = Boolean(data.banned);
  const activatedResponse = Boolean(data.activated);
  const betaResponse = Boolean(data.beta_user ?? data.betaUser ?? false);

  if (process.env.NODE_ENV !== 'production') {
    console.log('Disabled:', disabledResponse);
    console.log('Banned:', bannedResponse);
    console.log('Activated:', activatedResponse);
    console.log('BetaUser:', betaResponse);
  }

  setUserDisabled(
    disabledResponse,
    bannedResponse,
    activatedResponse,
    betaResponse,
  );
  return {
    disabled: disabledResponse,
    banned: bannedResponse,
    activated: activatedResponse,
    betaUser: betaResponse,
  };
}
